import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Mail, Users, List as ListIcon, Send, Plus, Trash2, Download, Eye, Star, StarOff, Check, X, Tag, Loader2, Zap, RefreshCw } from "lucide-react";

const AUTO_RULE_OPTIONS = [
  { value: "", label: "None (manual only)" },
  { value: "role:creator", label: "role:creator — all creators" },
  { value: "role:viewer", label: "role:viewer — all viewers" },
  { value: "role:admin", label: "role:admin — all admins" },
  { value: "subscription:active", label: "subscription:active — currently paid up" },
  { value: "subscription:expired", label: "subscription:expired — lapsed / cancelled" },
  { value: "has_channel", label: "has_channel — creators who own a channel" },
  { value: "has_approved_movie", label: "has_approved_movie — published creators" },
];

const SEGMENTS = [
  { key: "all_active", label: "All active subscribers" },
  { key: "last_30_days", label: "Subscribed in the last 30 days" },
  { key: "has_account", label: "Has a Christoreel account" },
  { key: "active_members", label: "Active paying members" },
  { key: "creators", label: "Creators only" },
];

export default function NewsletterAdmin() {
  const [subtab, setSubtab] = useState("subscribers");
  return (
    <div className="space-y-6 mt-6" data-testid="admin-newsletter-content">
      <div className="flex border-b border-white/10 overflow-x-auto scrollbar-thin">
        {[
          { k: "subscribers", label: "Subscribers", icon: Users, testid: "newsletter-subtab-subscribers" },
          { k: "lists", label: "Lists", icon: ListIcon, testid: "newsletter-subtab-lists" },
          { k: "senders", label: "Senders", icon: Mail, testid: "newsletter-subtab-senders" },
          { k: "campaigns", label: "Campaigns", icon: Send, testid: "newsletter-subtab-campaigns" },
        ].map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.k}
              onClick={() => setSubtab(t.k)}
              data-testid={t.testid}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition whitespace-nowrap ${subtab === t.k ? "border-[#E65F2B] text-white" : "border-transparent text-white/40 hover:text-white"}`}
            >
              <Icon className="h-4 w-4" /> {t.label}
            </button>
          );
        })}
      </div>
      {subtab === "subscribers" && <SubscribersPanel />}
      {subtab === "lists" && <ListsPanel />}
      {subtab === "senders" && <SendersPanel />}
      {subtab === "campaigns" && <CampaignsPanel />}
    </div>
  );
}

// ---------------- Subscribers ----------------
function SubscribersPanel() {
  const [data, setData] = useState({ total: 0, active: 0, items: [] });
  const [lists, setLists] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [addEmail, setAddEmail] = useState("");
  const [addName, setAddName] = useState("");
  const [openManage, setOpenManage] = useState(null); // subscriber_id or null
  const [rowBusy, setRowBusy] = useState({});         // sub_id -> boolean

  const staticLists = lists.filter((l) => l.kind === "static");
  const listById = Object.fromEntries(lists.map((l) => [l.id, l]));

  const loadSubs = () => api.get(`/admin/newsletter/subscribers${q ? `?q=${encodeURIComponent(q)}` : ""}`)
    .then((r) => setData(r.data)).catch(() => {});
  const loadLists = () => api.get("/admin/newsletter/lists").then((r) => setLists(r.data || [])).catch(() => {});
  useEffect(() => { loadSubs(); }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadLists(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async (e) => {
    e.preventDefault();
    if (!addEmail.trim()) return;
    setBusy(true);
    try {
      await api.post("/admin/newsletter/subscribers", { email: addEmail.trim(), name: addName.trim() || null });
      toast.success("Subscriber added.");
      setAddEmail(""); setAddName("");
      loadSubs(); loadLists();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't add."); }
    finally { setBusy(false); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.email} from the subscriber list?`)) return;
    try { await api.delete(`/admin/newsletter/subscribers/${s.id}`); toast.success("Removed."); loadSubs(); loadLists(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't remove."); }
  };
  const exportCsv = () => {
    const url = `${api.defaults.baseURL}/admin/newsletter/subscribers/export.csv`;
    window.open(url, "_blank");
  };

  const toggleListMembership = async (sub, listId, isMember) => {
    setRowBusy((b) => ({ ...b, [sub.id]: true }));
    try {
      const url = `/admin/newsletter/lists/${listId}/${isMember ? "remove" : "add"}`;
      const { data } = await api.post(url, { subscriber_ids: [sub.id] });
      if (!isMember && data.mutex_removed > 0) {
        toast.success(`Added — also removed from ${data.mutex_removed} sibling list${data.mutex_removed === 1 ? "" : "s"} in group "${data.mutex_group}".`);
      } else {
        toast.success(isMember ? "Removed from list." : "Added to list.");
      }
      loadSubs(); loadLists();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update.");
    } finally {
      setRowBusy((b) => ({ ...b, [sub.id]: false }));
    }
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className="card-surface p-4"><p className="text-eyebrow">Total</p><p className="font-display text-3xl font-black">{data.total}</p></div>
        <div className="card-surface p-4"><p className="text-eyebrow">Active</p><p className="font-display text-3xl font-black text-green-400">{data.active}</p></div>
        <div className="card-surface p-4"><p className="text-eyebrow">Unsubscribed</p><p className="font-display text-3xl font-black text-white/40">{data.total - data.active}</p></div>
      </div>

      <form onSubmit={add} className="card-surface p-4 flex flex-col sm:flex-row gap-2" data-testid="add-subscriber-form">
        <input value={addEmail} onChange={(e) => setAddEmail(e.target.value)} type="email" required placeholder="new@example.com" className="input-dark flex-1 !py-2" data-testid="add-subscriber-email" disabled={busy} />
        <input value={addName} onChange={(e) => setAddName(e.target.value)} placeholder="Name (optional)" className="input-dark flex-1 !py-2" disabled={busy} />
        <button type="submit" disabled={busy || !addEmail.trim()} className="ember-btn disabled:opacity-40" data-testid="add-subscriber-submit"><Plus className="h-4 w-4" /> Add</button>
      </form>

      <div className="flex items-center gap-3">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email or name..." className="input-dark flex-1 !py-2 text-sm" data-testid="subscriber-search" />
        <button onClick={exportCsv} className="ghost-btn !py-2 !px-4 text-sm" data-testid="subscribers-export-csv"><Download className="h-4 w-4" /> CSV</button>
      </div>

      {data.items.length === 0 ? (
        <div className="card-surface p-10 text-center text-white/50" data-testid="subscribers-empty">No subscribers yet.</div>
      ) : (
        <div className="space-y-2" data-testid="subscribers-list">
          {data.items.map((s) => {
            const memberOfIds = s.list_ids || [];
            // Filter to STATIC lists only — admin can't add to a dynamic list (they auto-populate).
            const memberOfStatic = memberOfIds.map((id) => listById[id]).filter((l) => l && l.kind === "static");
            const availableToAdd = staticLists.filter((l) => !memberOfIds.includes(l.id));
            const isManaging = openManage === s.id;
            return (
              <div key={s.id} className="card-surface p-3" data-testid={`subscriber-row-${s.id}`}>
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium break-all">{s.email}</p>
                    <p className="text-xs text-white/40">{s.name || "—"} · via {s.source} · {s.subscribed_at?.slice(0, 10)}</p>

                    {/* Always-visible list-membership column (static lists only).
                        In manage mode we ALSO show grey "+ list" chips for adds. */}
                    <div className="flex flex-wrap gap-1.5 mt-2 items-center" data-testid={`subscriber-lists-${s.id}`}>
                      {memberOfStatic.length === 0 && !isManaging && (
                        <span className="text-[11px] text-white/25 italic">no list memberships</span>
                      )}
                      {memberOfStatic.map((l) => (
                        <button
                          key={l.id}
                          onClick={() => isManaging && toggleListMembership(s, l.id, true)}
                          disabled={rowBusy[s.id] || !isManaging}
                          className={`group inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#E65F2B]/10 border border-[#E65F2B]/25 text-[#E65F2B] text-[11px] transition disabled:opacity-70 ${isManaging ? "cursor-pointer hover:bg-red-500/15 hover:border-red-500/40 hover:text-red-400" : "cursor-default"}`}
                          data-testid={`sub-${s.id}-list-remove-${l.id}`}
                          title={isManaging ? `Remove from ${l.name}` : l.name}
                        >
                          <Tag className="h-2.5 w-2.5" />
                          <span className="truncate max-w-[140px]">{l.name}</span>
                          {isManaging && <X className="h-3 w-3 opacity-40 group-hover:opacity-100 transition" />}
                        </button>
                      ))}
                      {isManaging && availableToAdd.length > 0 && availableToAdd.map((l) => (
                        <button
                          key={l.id}
                          onClick={() => toggleListMembership(s, l.id, false)}
                          disabled={rowBusy[s.id]}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/[0.03] border border-white/10 text-white/50 text-[11px] hover:bg-[#22C55E]/10 hover:border-[#22C55E]/30 hover:text-[#22C55E] transition disabled:opacity-40"
                          data-testid={`sub-${s.id}-list-add-${l.id}`}
                          title={l.mutex_group ? `Add to ${l.name} (mutex: ${l.mutex_group})` : `Add to ${l.name}`}
                        >
                          <Plus className="h-2.5 w-2.5" />
                          <span className="truncate max-w-[140px]">{l.name}</span>
                          {l.mutex_group && <span className="text-[9px] text-[#EAB308]/70 ml-0.5">⇔</span>}
                        </button>
                      ))}
                      {isManaging && availableToAdd.length === 0 && (
                        <span className="text-[11px] text-white/30 italic">on every static list</span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {!s.is_active && <span className="badge-pill text-[10px] bg-red-500/15 text-red-300 border border-red-500/30">unsubscribed</span>}

                    {/* Manage-lists toggle */}
                    {staticLists.length > 0 && (
                      <button
                        onClick={() => setOpenManage(isManaging ? null : s.id)}
                        className={`ghost-btn !py-1.5 !px-3 text-xs ${isManaging ? "border-[#E65F2B]/40 text-[#E65F2B]" : ""}`}
                        data-testid={`subscriber-manage-${s.id}`}
                        title="Add / remove lists"
                      >
                        {rowBusy[s.id] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Tag className="h-3.5 w-3.5" />}
                        {isManaging ? "Done" : "Lists"}
                      </button>
                    )}
                    <button onClick={() => remove(s)} className="ghost-btn !py-1.5 !px-3 text-xs hover:border-red-500/50 hover:text-red-400" data-testid={`subscriber-remove-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------- Lists ----------------
function ListsPanel() {
  const [lists, setLists] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [kind, setKind] = useState("static");
  const [segment, setSegment] = useState("all_active");
  const [busy, setBusy] = useState(false);
  const [mutex, setMutex] = useState("");
  const [autoRule, setAutoRule] = useState("");
  const [membersOf, setMembersOf] = useState(null); // list_id when the members modal is open
  const [memberBusy, setMemberBusy] = useState({}); // sub_id -> boolean
  const [addQ, setAddQ] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editMutex, setEditMutex] = useState("");
  const [editAutoRule, setEditAutoRule] = useState("");
  const [editBusy, setEditBusy] = useState(false);
  const [reconcilingId, setReconcilingId] = useState(null);

  const load = () => api.get("/admin/newsletter/lists").then((r) => setLists(r.data || [])).catch(() => {});
  const loadSubs = () => api.get("/admin/newsletter/subscribers?limit=500").then((r) => setSubscribers(r.data.items || [])).catch(() => {});
  useEffect(() => { load(); loadSubs(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post("/admin/newsletter/lists", {
        name: name.trim(), description: desc.trim(),
        kind, segment: kind === "dynamic" ? segment : null,
        mutex_group: kind === "static" ? (mutex.trim() || null) : null,
        auto_rule: kind === "static" ? (autoRule.trim() || null) : null,
      });
      toast.success(autoRule ? "List created & auto-populated." : "List created.");
      setName(""); setDesc(""); setMutex(""); setAutoRule("");
      load(); loadSubs();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't create."); }
    finally { setBusy(false); }
  };

  const startEdit = (l) => {
    setEditingId(l.id);
    setEditName(l.name);
    setEditDesc(l.description || "");
    setEditMutex(l.mutex_group || "");
    setEditAutoRule(l.auto_rule || "");
  };
  const cancelEdit = () => { setEditingId(null); setEditName(""); setEditDesc(""); setEditMutex(""); setEditAutoRule(""); };
  const saveEdit = async () => {
    if (!editName.trim()) return;
    setEditBusy(true);
    try {
      const target = lists.find((l) => l.id === editingId);
      await api.patch(`/admin/newsletter/lists/${editingId}`, {
        name: editName.trim(),
        description: editDesc.trim(),
        kind: target?.kind || "static",
        segment: target?.segment || null,
        mutex_group: editMutex.trim() || null,
        auto_rule: editAutoRule.trim() || null,
      });
      toast.success(editAutoRule ? "List updated & re-reconciled." : "List updated.");
      cancelEdit();
      load(); loadSubs();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save."); }
    finally { setEditBusy(false); }
  };

  const reconcile = async (l) => {
    setReconcilingId(l.id);
    try {
      const { data } = await api.post(`/admin/newsletter/lists/${l.id}/reconcile`);
      toast.success(`Reconciled: +${data.added} added, -${data.removed} removed${data.mutex_removed ? ` · ${data.mutex_removed} mutex-pulls` : ""}.`);
      load(); loadSubs();
    } catch (e) { toast.error(e.response?.data?.detail || "Reconcile failed."); }
    finally { setReconcilingId(null); }
  };
  const remove = async (l) => {
    if (l.is_default) { toast.error("Can't delete the default list."); return; }
    if (!window.confirm(`Delete list "${l.name}"?`)) return;
    try { await api.delete(`/admin/newsletter/lists/${l.id}`); toast.success("Deleted."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't delete."); }
  };

  const addToList = async (listId, subId) => {
    setMemberBusy((b) => ({ ...b, [subId]: true }));
    try {
      await api.post(`/admin/newsletter/lists/${listId}/add`, { subscriber_ids: [subId] });
      toast.success("Added.");
      load(); loadSubs();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't add."); }
    finally { setMemberBusy((b) => ({ ...b, [subId]: false })); }
  };
  const removeFromList = async (listId, subId) => {
    setMemberBusy((b) => ({ ...b, [subId]: true }));
    try {
      await api.post(`/admin/newsletter/lists/${listId}/remove`, { subscriber_ids: [subId] });
      toast.success("Removed.");
      load(); loadSubs();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't remove."); }
    finally { setMemberBusy((b) => ({ ...b, [subId]: false })); }
  };

  const activeList = membersOf ? lists.find((l) => l.id === membersOf) : null;
  const members = activeList ? subscribers.filter((s) => (s.list_ids || []).includes(activeList.id)) : [];
  const nonMembers = activeList
    ? subscribers.filter((s) => s.is_active && !(s.list_ids || []).includes(activeList.id))
      .filter((s) => !addQ.trim() ||
        s.email.toLowerCase().includes(addQ.toLowerCase()) ||
        (s.name || "").toLowerCase().includes(addQ.toLowerCase()))
      .slice(0, 30)
    : [];

  return (
    <div className="space-y-5">
      <form onSubmit={create} className="card-surface p-5 space-y-3" data-testid="add-list-form">
        <div className="grid sm:grid-cols-2 gap-3">
          <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="List name" className="input-dark" data-testid="list-name-input" disabled={busy} />
          <select value={kind} onChange={(e) => setKind(e.target.value)} className="input-dark" data-testid="list-kind-select" disabled={busy}>
            <option value="static">Static (manual members)</option>
            <option value="dynamic">Dynamic (auto-segment)</option>
          </select>
        </div>
        <input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Description (optional)" className="input-dark" disabled={busy} />
        {kind === "dynamic" && (
          <select value={segment} onChange={(e) => setSegment(e.target.value)} className="input-dark" data-testid="list-segment-select" disabled={busy}>
            {SEGMENTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        )}
        {kind === "static" && (
          <div className="space-y-2">
            <div>
              <input
                value={mutex}
                onChange={(e) => setMutex(e.target.value)}
                placeholder="Mutex group (optional) — e.g. 'creator_status'"
                className="input-dark"
                data-testid="list-mutex-input"
                disabled={busy}
              />
              <p className="text-[11px] text-white/40 mt-1">
                Lists sharing the same mutex group are mutually exclusive — adding a subscriber to one auto-removes them from the others.
              </p>
            </div>
            <div>
              <select
                value={autoRule}
                onChange={(e) => setAutoRule(e.target.value)}
                className="input-dark"
                data-testid="list-auto-rule-select"
                disabled={busy}
              >
                {AUTO_RULE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <p className="text-[11px] text-white/40 mt-1">
                Auto-populate rule — membership stays in sync with matching users (respects unsubscribes and mutex).
              </p>
            </div>
          </div>
        )}
        <button type="submit" disabled={busy || !name.trim()} className="ember-btn disabled:opacity-40" data-testid="add-list-submit"><Plus className="h-4 w-4" /> Create list</button>
      </form>

      <div className="space-y-2" data-testid="lists-list">
        {lists.length === 0 ? (
          <div className="card-surface p-10 text-center text-white/50">No lists yet.</div>
        ) : (
          lists.map((l) => {
            const isEditing = editingId === l.id;
            if (isEditing) {
              return (
                <div key={l.id} className="card-surface p-4 space-y-2" data-testid={`list-edit-${l.id}`}>
                  <input value={editName} onChange={(e) => setEditName(e.target.value)} className="input-dark" placeholder="List name" disabled={editBusy} data-testid="list-edit-name" />
                  <input value={editDesc} onChange={(e) => setEditDesc(e.target.value)} className="input-dark" placeholder="Description" disabled={editBusy} />
                  {l.kind === "static" && (
                    <>
                      <input value={editMutex} onChange={(e) => setEditMutex(e.target.value)} className="input-dark" placeholder="Mutex group (blank = none)" disabled={editBusy} data-testid="list-edit-mutex" />
                      <select value={editAutoRule} onChange={(e) => setEditAutoRule(e.target.value)} className="input-dark" disabled={editBusy} data-testid="list-edit-auto-rule">
                        {AUTO_RULE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </>
                  )}
                  <div className="flex gap-2">
                    <button onClick={saveEdit} disabled={editBusy || !editName.trim()} className="ember-btn disabled:opacity-40" data-testid={`list-edit-save-${l.id}`}>
                      {editBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save
                    </button>
                    <button onClick={cancelEdit} className="ghost-btn" disabled={editBusy}>Cancel</button>
                  </div>
                </div>
              );
            }
            return (
              <div key={l.id} className="card-surface p-4 flex items-center gap-3" data-testid={`list-row-${l.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium">{l.name}</p>
                    {l.is_default && <span className="badge-pill text-[10px] bg-[#E65F2B]/15 text-[#E65F2B] border border-[#E65F2B]/30">Default</span>}
                    <span className="badge-pill text-[10px] bg-white/5 text-white/60 border border-white/10">{l.kind === "dynamic" ? "auto" : "manual"}</span>
                    {l.mutex_group && (
                      <span className="badge-pill text-[10px] bg-[#EAB308]/10 text-[#EAB308] border border-[#EAB308]/25" data-testid={`list-mutex-badge-${l.id}`} title={`Mutually exclusive with lists in group "${l.mutex_group}"`}>
                        ⇔ {l.mutex_group}
                      </span>
                    )}
                    {l.auto_rule && (
                      <span className="badge-pill text-[10px] bg-[#22C55E]/10 text-[#22C55E] border border-[#22C55E]/25" data-testid={`list-auto-badge-${l.id}`} title={`Auto-populated from user data — rule: ${l.auto_rule}`}>
                        <Zap className="h-2.5 w-2.5 mr-0.5 inline" />{l.auto_rule}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-white/40 mt-1">
                    {l.subscriber_count} member{l.subscriber_count === 1 ? "" : "s"} {l.description ? `· ${l.description}` : ""}
                    {l.last_reconciled_at && <span className="text-white/30"> · synced {new Date(l.last_reconciled_at).toLocaleString()}</span>}
                  </p>
                </div>
                {l.auto_rule && (
                  <button onClick={() => reconcile(l)} disabled={reconcilingId === l.id} className="ghost-btn !py-1.5 !px-3 text-xs hover:border-[#22C55E]/50 hover:text-[#22C55E]" data-testid={`list-reconcile-${l.id}`}>
                    {reconcilingId === l.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Reconcile
                  </button>
                )}
                {l.kind === "static" && (
                  <button onClick={() => { setMembersOf(l.id); setAddQ(""); }} className="ghost-btn !py-1.5 !px-3 text-xs hover:border-[#E65F2B]/40 hover:text-[#E65F2B]" data-testid={`list-members-${l.id}`}>
                    <Users className="h-3.5 w-3.5" /> Members
                  </button>
                )}
                {l.kind === "static" && (
                  <button onClick={() => startEdit(l)} className="ghost-btn !py-1.5 !px-3 text-xs" data-testid={`list-edit-${l.id}-btn`} title="Edit name, description, mutex group, auto-rule">
                    <Tag className="h-3.5 w-3.5" /> Edit
                  </button>
                )}
                {!l.is_default && (
                  <button onClick={() => remove(l)} className="ghost-btn !py-1.5 !px-3 text-xs hover:border-red-500/50 hover:text-red-400" data-testid={`list-delete-${l.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Members modal */}
      {activeList && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={(e) => e.target === e.currentTarget && setMembersOf(null)}
          data-testid="list-members-modal"
        >
          <div className="card-surface w-full max-w-2xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-white/5 shrink-0">
              <div className="min-w-0">
                <p className="text-eyebrow">Members</p>
                <h3 className="font-display text-xl font-bold truncate">{activeList.name}</h3>
                <p className="text-xs text-white/40 mt-0.5">{members.length} subscriber{members.length === 1 ? "" : "s"}</p>
              </div>
              <button onClick={() => setMembersOf(null)} className="ghost-btn !py-1.5 !px-3" data-testid="list-members-close">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="p-5 overflow-y-auto flex-1 space-y-5">
              {/* Existing members */}
              <div>
                <p className="text-eyebrow mb-2">On this list ({members.length})</p>
                {members.length === 0 ? (
                  <p className="text-sm text-white/40 py-4">No members yet — add some below.</p>
                ) : (
                  <div className="space-y-1.5" data-testid="list-members-list">
                    {members.map((s) => (
                      <div key={s.id} className="flex items-center gap-3 p-2 rounded-md bg-white/[0.02] border border-white/5" data-testid={`list-member-${s.id}`}>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm truncate">{s.email}</p>
                          <p className="text-[11px] text-white/40">{s.name || "—"}</p>
                        </div>
                        <button
                          onClick={() => removeFromList(activeList.id, s.id)}
                          disabled={memberBusy[s.id]}
                          className="ghost-btn !py-1 !px-2 text-[11px] hover:border-red-500/50 hover:text-red-400"
                          data-testid={`list-member-remove-${s.id}`}
                        >
                          {memberBusy[s.id] ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Add new members */}
              <div>
                <p className="text-eyebrow mb-2">Add subscribers</p>
                <input
                  value={addQ}
                  onChange={(e) => setAddQ(e.target.value)}
                  placeholder="Search by email or name…"
                  className="input-dark !py-2 text-sm mb-2"
                  data-testid="list-members-add-search"
                />
                {nonMembers.length === 0 ? (
                  <p className="text-sm text-white/40 py-4">
                    {addQ.trim() ? "No matching subscribers." : subscribers.length === 0 ? "No subscribers to add." : "Type to search."}
                  </p>
                ) : (
                  <div className="space-y-1.5 max-h-72 overflow-y-auto" data-testid="list-members-add-list">
                    {nonMembers.map((s) => (
                      <div key={s.id} className="flex items-center gap-3 p-2 rounded-md bg-white/[0.02] border border-white/5" data-testid={`list-nonmember-${s.id}`}>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm truncate">{s.email}</p>
                          <p className="text-[11px] text-white/40">{s.name || "—"}</p>
                        </div>
                        <button
                          onClick={() => addToList(activeList.id, s.id)}
                          disabled={memberBusy[s.id]}
                          className="ghost-btn !py-1 !px-2 text-[11px] hover:border-[#22C55E]/50 hover:text-[#22C55E]"
                          data-testid={`list-member-add-${s.id}`}
                        >
                          {memberBusy[s.id] ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                          Add
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------- Senders ----------------
function SendersPanel() {
  const [senders, setSenders] = useState([]);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/newsletter/senders").then((r) => setSenders(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/newsletter/senders", { email: email.trim(), name: name.trim(), is_default: senders.length === 0 });
      toast.success("Sender added.");
      setEmail(""); setName("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't add."); }
    finally { setBusy(false); }
  };
  const setDefault = async (s) => {
    try { await api.post(`/admin/newsletter/senders/${s.id}/default`); toast.success(`${s.email} is now the default sender.`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't update."); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Delete sender ${s.email}?`)) return;
    try { await api.delete(`/admin/newsletter/senders/${s.id}`); toast.success("Deleted."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't delete."); }
  };
  return (
    <div className="space-y-5">
      <form onSubmit={add} className="card-surface p-4 grid sm:grid-cols-3 gap-2" data-testid="add-sender-form">
        <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="from@christoreel.com" className="input-dark !py-2" data-testid="sender-email-input" disabled={busy} />
        <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="From name" className="input-dark !py-2" data-testid="sender-name-input" disabled={busy} />
        <button type="submit" disabled={busy || !email.trim() || !name.trim()} className="ember-btn disabled:opacity-40" data-testid="add-sender-submit"><Plus className="h-4 w-4" /> Add sender</button>
      </form>
      <div className="space-y-2" data-testid="senders-list">
        {senders.map((s) => (
          <div key={s.id} className="card-surface p-4 flex items-center gap-3" data-testid={`sender-row-${s.id}`}>
            <div className="min-w-0 flex-1">
              <p className="font-medium">{s.name} <span className="text-white/40 font-normal">&lt;{s.email}&gt;</span></p>
              {s.is_default && <span className="badge-pill text-[10px] bg-[#E65F2B]/15 text-[#E65F2B] border border-[#E65F2B]/30 mt-1 inline-flex"><Star className="h-3 w-3 mr-1" />Default</span>}
            </div>
            {!s.is_default && (
              <button onClick={() => setDefault(s)} className="ghost-btn !py-1.5 !px-3 text-xs" data-testid={`sender-set-default-${s.id}`}><StarOff className="h-3.5 w-3.5" /> Set default</button>
            )}
            <button onClick={() => remove(s)} className="ghost-btn !py-1.5 !px-3 text-xs hover:border-red-500/50 hover:text-red-400" data-testid={`sender-delete-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------- Campaigns ----------------
function CampaignsPanel() {
  const [view, setView] = useState("list"); // list | compose
  const [editing, setEditing] = useState(null); // campaign object
  const [campaigns, setCampaigns] = useState([]);
  const load = () => api.get("/admin/newsletter/campaigns").then((r) => setCampaigns(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const openNew = () => { setEditing(null); setView("compose"); };
  const openEdit = (c) => { setEditing(c); setView("compose"); };
  const back = () => { setView("list"); setEditing(null); load(); };
  if (view === "compose") return <CampaignComposer initial={editing} onBack={back} />;
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h4 className="font-display text-lg font-bold">Campaigns</h4>
        <button onClick={openNew} className="ember-btn" data-testid="new-campaign-button"><Plus className="h-4 w-4" /> New campaign</button>
      </div>
      {campaigns.length === 0 ? (
        <div className="card-surface p-10 text-center text-white/50" data-testid="campaigns-empty">No campaigns yet. Compose your first one.</div>
      ) : (
        <div className="space-y-2" data-testid="campaigns-list">
          {campaigns.map((c) => (
            <div key={c.id} className="card-surface p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`campaign-row-${c.id}`}>
              <div className="min-w-0 flex-1">
                <p className="font-medium">{c.subject}</p>
                <p className="text-xs text-white/40 mt-1">
                  {c.status === "draft" ? "Draft" : c.status === "sending" || c.status === "queued" ? "Sending..." : c.status === "sent" ? `Sent · ${c.sent_at?.slice(0, 10)}` : c.status}
                  {" · "}
                  <span className="tabular-nums">{c.stats?.sent || 0} sent · {c.stats?.opened || 0} opens · {c.stats?.clicked || 0} clicks</span>
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => openEdit(c)} className="ghost-btn !py-2 !px-4 text-sm" data-testid={`campaign-open-${c.id}`}><Eye className="h-4 w-4" /> {c.status === "draft" ? "Edit" : "View"}</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CampaignComposer({ initial, onBack }) {
  const [subject, setSubject] = useState(initial?.subject || "");
  const [bodyMd, setBodyMd] = useState(initial?.body_md || "Hi {{name}},\n\nWrite your update here. **Markdown supported** — [links](https://christoreel.com), lists, bold.\n\n- New film released this week\n- Featured filmmaker interview\n- Community update\n\nWith gratitude,\nChristoreel");
  const [senders, setSenders] = useState([]);
  const [lists, setLists] = useState([]);
  const [senderId, setSenderId] = useState(initial?.sender_id || "");
  const [listIds, setListIds] = useState(initial?.list_ids || []);
  const [previewHtml, setPreviewHtml] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [campaignId, setCampaignId] = useState(initial?.id || null);
  const [status, setStatus] = useState(initial?.status || "draft");

  useEffect(() => {
    api.get("/admin/newsletter/senders").then((r) => {
      const list = r.data || [];
      setSenders(list);
      if (!senderId && list.length) setSenderId(list.find((s) => s.is_default)?.id || list[0].id);
    });
    api.get("/admin/newsletter/lists").then((r) => setLists(r.data || []));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isReadOnly = status !== "draft";

  const saveDraft = async () => {
    if (!subject.trim() || !bodyMd.trim() || !senderId || listIds.length === 0) {
      toast.error("Subject, body, sender and at least one list are required.");
      return null;
    }
    setBusy(true);
    try {
      if (campaignId) {
        const { data } = await api.patch(`/admin/newsletter/campaigns/${campaignId}`, { subject, body_md: bodyMd, sender_id: senderId, list_ids: listIds });
        setStatus(data.status);
        toast.success("Draft saved.");
        return data.id;
      } else {
        const { data } = await api.post("/admin/newsletter/campaigns", { subject, body_md: bodyMd, sender_id: senderId, list_ids: listIds });
        setCampaignId(data.id); setStatus(data.status);
        toast.success("Draft created.");
        return data.id;
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save."); return null; }
    finally { setBusy(false); }
  };

  const preview = async () => {
    const id = campaignId || (await saveDraft());
    if (!id) return;
    try {
      const { data } = await api.get(`/admin/newsletter/campaigns/${id}/preview`);
      setPreviewHtml(data);
    } catch (e) { toast.error("Couldn't load preview."); }
  };

  const sendTest = async () => {
    if (!testEmail.trim()) { toast.error("Enter a test email address."); return; }
    const id = campaignId || (await saveDraft());
    if (!id) return;
    setBusy(true);
    try {
      await api.post(`/admin/newsletter/campaigns/${id}/send-test`, { to_email: testEmail.trim() });
      toast.success(`Test sent to ${testEmail.trim()}.`);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't send test."); }
    finally { setBusy(false); }
  };

  const sendLive = async () => {
    const id = campaignId || (await saveDraft());
    if (!id) return;
    const totalTargeted = lists.filter((l) => listIds.includes(l.id)).reduce((s, l) => s + (l.subscriber_count || 0), 0);
    if (!window.confirm(`Send this campaign live to approximately ${totalTargeted} subscribers now? This can't be undone.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/newsletter/campaigns/${id}/send`);
      toast.success("Campaign queued for sending.");
      onBack();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't send."); }
    finally { setBusy(false); }
  };

  const toggleList = (id) => setListIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  return (
    <div className="space-y-5" data-testid="campaign-composer">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="ghost-btn !py-1.5 !px-3 text-xs" data-testid="composer-back">← Back to campaigns</button>
        {status !== "draft" && <span className="badge-pill text-[10px] bg-white/5 text-white/60 border border-white/10">{status}</span>}
      </div>
      <div className="grid lg:grid-cols-[1fr_320px] gap-5">
        <div className="space-y-4">
          <div>
            <label className="text-eyebrow mb-2 block">Subject</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} disabled={isReadOnly || busy} className="input-dark" placeholder="This week on Christoreel — 3 new films" data-testid="composer-subject" />
          </div>
          <div>
            <label className="text-eyebrow mb-2 block">Body · Markdown supported</label>
            <textarea value={bodyMd} onChange={(e) => setBodyMd(e.target.value)} disabled={isReadOnly || busy} rows={16} className="input-dark font-mono text-sm resize-none" data-testid="composer-body" />
            <p className="text-xs text-white/40 mt-1">Supports **bold**, *italic*, [links](url), bullet lists, headings (## Title). Content is sanitized before send.</p>
          </div>
          {previewHtml && (
            <div className="card-surface p-2" data-testid="composer-preview">
              <div className="flex items-center justify-between p-2">
                <p className="text-xs text-white/40">Preview</p>
                <button onClick={() => setPreviewHtml("")} className="text-white/40 hover:text-white text-xs" data-testid="composer-preview-close"><X className="h-4 w-4" /></button>
              </div>
              <iframe title="preview" srcDoc={previewHtml} className="w-full h-[600px] rounded bg-white" />
            </div>
          )}
        </div>
        <div className="space-y-4">
          <div className="card-surface p-4 space-y-3">
            <label className="text-eyebrow block">From</label>
            <select value={senderId} onChange={(e) => setSenderId(e.target.value)} disabled={isReadOnly || busy} className="input-dark" data-testid="composer-sender">
              {senders.map((s) => <option key={s.id} value={s.id}>{s.name} &lt;{s.email}&gt;</option>)}
            </select>
          </div>
          <div className="card-surface p-4 space-y-3">
            <label className="text-eyebrow block">Lists · {listIds.length} selected</label>
            <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="composer-lists">
              {lists.map((l) => {
                const active = listIds.includes(l.id);
                return (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => !isReadOnly && toggleList(l.id)}
                    disabled={isReadOnly || busy}
                    className={`w-full text-left flex items-center gap-2 p-2 rounded border ${active ? "border-[#E65F2B] bg-[#E65F2B]/10" : "border-white/10 hover:border-white/30"} disabled:opacity-60 disabled:cursor-not-allowed`}
                    data-testid={`composer-list-${l.id}`}
                  >
                    <span className={`h-4 w-4 rounded shrink-0 border ${active ? "bg-[#E65F2B] border-[#E65F2B]" : "border-white/30"} flex items-center justify-center`}>
                      {active && <Check className="h-3 w-3 text-white" />}
                    </span>
                    <span className="text-sm flex-1 truncate">{l.name}</span>
                    <span className="text-xs text-white/40">{l.subscriber_count}</span>
                  </button>
                );
              })}
            </div>
          </div>
          {!isReadOnly && (
            <div className="card-surface p-4 space-y-3">
              <label className="text-eyebrow block">Test send</label>
              <div className="flex gap-2">
                <input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} type="email" placeholder="you@example.com" className="input-dark flex-1 !py-2 text-sm" data-testid="composer-test-email" disabled={busy} />
                <button onClick={sendTest} disabled={busy || !testEmail.trim()} className="ghost-btn !py-2 !px-3 text-sm disabled:opacity-40" data-testid="composer-send-test">Send test</button>
              </div>
            </div>
          )}
          <div className="flex flex-col gap-2">
            <button onClick={preview} disabled={busy} className="ghost-btn justify-center" data-testid="composer-preview-btn"><Eye className="h-4 w-4" /> Preview</button>
            {!isReadOnly && (
              <>
                <button onClick={saveDraft} disabled={busy} className="ghost-btn justify-center" data-testid="composer-save">Save draft</button>
                <button onClick={sendLive} disabled={busy} className="ember-btn justify-center" data-testid="composer-send"><Send className="h-4 w-4" /> Send live</button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
